<template>
  <fieldset
    :id="computedId"
    :disabled="props.disabled"
    :aria-invalid="computedAriaInvalid"
    :aria-labelledby="isFieldset && isHorizontal ? labelId : undefined"
    v-bind="$attrs"
    :class="[stateClass, {'was-validated': props.validated}]"
    class="b-form-group"
  >
    <!-- End of definitions -->
    <BFormRow v-if="isHorizontal">
      <BFormGroupLabel
        v-if="labelShowing"
        v-bind="labelComponentProps"
        @legend-click="onLegendClick"
      >
        <template v-if="slots.label" #label>
          <slot name="label" />
        </template>
      </BFormGroupLabel>
      <BCol v-bind="contentColProps" ref="_content">
        <slot
          :id="computedId"
          :aria-describedby="computedAriaDescribedby"
          :description-id="descriptionId"
          :label-id="labelId"
        />
        <BFormGroupContent v-bind="contentComponentProps">
          <template v-if="slots['invalid-feedback']" #invalid-feedback>
            <slot name="invalid-feedback" />
          </template>
          <template v-if="slots['valid-feedback']" #valid-feedback>
            <slot name="valid-feedback" />
          </template>
          <template v-if="slots.description" #description>
            <slot name="description" />
          </template>
        </BFormGroupContent>
      </BCol>
    </BFormRow>
    <template v-else>
      <div v-if="isFloating" ref="_content" class="form-floating">
        <slot
          :id="computedId"
          :aria-describedby="computedAriaDescribedby"
          :description-id="descriptionId"
          :label-id="labelId"
        />
        <BFormGroupLabel
          v-if="labelShowing"
          v-bind="labelComponentProps"
          @legend-click="onLegendClick"
        >
          <template v-if="slots.label" #label>
            <slot name="label" />
          </template>
        </BFormGroupLabel>
        <BFormGroupContent v-bind="contentComponentProps">
          <template v-if="slots['invalid-feedback']" #invalid-feedback>
            <slot name="invalid-feedback" />
          </template>
          <template v-if="slots['valid-feedback']" #valid-feedback>
            <slot name="valid-feedback" />
          </template>
          <template v-if="slots.description" #description>
            <slot name="description" />
          </template>
        </BFormGroupContent>
      </div>
      <template v-else>
        <BFormGroupLabel
          v-if="labelShowing"
          v-bind="labelComponentProps"
          @legend-click="onLegendClick"
        >
          <template v-if="slots.label" #label>
            <slot name="label" />
          </template>
        </BFormGroupLabel>
        <slot
          :id="computedId"
          :aria-describedby="computedAriaDescribedby"
          :description-id="descriptionId"
          :label-id="labelId"
        />
        <BFormGroupContent v-bind="contentComponentProps">
          <template v-if="slots['invalid-feedback']" #invalid-feedback>
            <slot name="invalid-feedback" />
          </template>
          <template v-if="slots['valid-feedback']" #valid-feedback>
            <slot name="valid-feedback" />
          </template>
          <template v-if="slots.description" #description>
            <slot name="description" />
          </template>
        </BFormGroupContent>
      </template>
    </template>
  </fieldset>
</template>

<script setup lang="ts">
import {computed, toRef, useTemplateRef} from 'vue'
import {useAriaInvalid} from '../../composables/useAriaInvalid'
import {attemptFocus, isVisible} from '../../utils/dom'
import BCol from '../BContainer/BCol.vue'
import BFormRow from '../BForm/BFormRow.vue'
import {suffixPropName} from '../../utils/props'
import {useStateClass} from '../../composables/useStateClass'
import {useId} from '../../composables/useId'
import type {BFormGroupProps, BFormGroupSlots} from '../../types'
import {useDefaults} from '../../composables/useDefaults'
import {useProvideFormGroupData} from '../../composables/useProvideFormGroupData'
import BFormGroupContent from '../BFormGroupContent.vue'
import BFormGroupLabel from '../BFormGroupLabel.vue'

defineOptions({
  inheritAttrs: false,
})

const INPUTS = ['input', 'select', 'textarea']

const _props = withDefaults(defineProps<BFormGroupProps>(), {
  ariaInvalid: undefined,
  contentCols: undefined,
  contentColsLg: undefined,
  contentColsMd: undefined,
  contentColsSm: undefined,
  contentColsXl: undefined,
  description: undefined,
  disabled: false,
  feedbackAriaLive: 'assertive',
  floating: false,
  id: undefined,
  invalidFeedback: undefined,
  label: undefined,
  labelAlign: undefined,
  labelAlignLg: undefined,
  labelAlignMd: undefined,
  labelAlignSm: undefined,
  labelAlignXl: undefined,
  labelClass: undefined,
  labelCols: undefined,
  labelColsLg: undefined,
  labelColsMd: undefined,
  labelColsSm: undefined,
  labelColsXl: undefined,
  labelFor: undefined,
  labelSize: undefined,
  labelVisuallyHidden: false,
  state: null,
  tooltip: false,
  validFeedback: undefined,
  validated: false,
})
const props = useDefaults(_props, 'BFormGroup')
const slots = defineSlots<BFormGroupSlots>()

const computedState = toRef(() => props.state)
const computedDisabled = toRef(() => props.disabled)
const {singleLabelTargetId} = useProvideFormGroupData({
  state: computedState,
  disabled: computedDisabled,
})
const computedLabelFor = computed(() => props.labelFor ?? singleLabelTargetId.value)

const breakPoints = ['xs', 'sm', 'md', 'lg', 'xl']

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getColProps = (props: any, prefix: string) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  breakPoints.reduce((result: any, breakpoint: string) => {
    const suffix = suffixPropName(breakpoint === 'xs' ? '' : breakpoint, `${prefix}Cols`)
    let propValue = props[suffix]
    propValue = propValue === '' ? true : propValue || false

    if (!(typeof propValue === 'boolean') && propValue !== 'auto') {
      const val = Number.parseInt(propValue)
      propValue = Number.isNaN(val) ? 0 : val
      propValue = propValue > 0 ? propValue : false
    }

    if (propValue) {
      if (breakpoint === 'xs') {
        result[typeof propValue === 'boolean' ? 'col' : 'cols'] = propValue
      } else {
        result[breakpoint || (typeof propValue === 'boolean' ? 'col' : 'cols')] = propValue
      }
    }
    return result
  }, {})

const content = useTemplateRef<HTMLDivElement | InstanceType<typeof BCol> | null>('_content')

const contentColProps = computed(() => getColProps(props, 'content'))
const labelAlignClasses = computed(() =>
  ((props: BFormGroupProps, prefix: string) =>
    breakPoints.reduce((result: string[], breakpoint) => {
      const suffix = suffixPropName(
        breakpoint === 'xs' ? '' : breakpoint,
        `${prefix}Align`
      ) as keyof BFormGroupProps
      const propValue: string = props[suffix] || null
      if (propValue) {
        if (breakpoint === 'xs') {
          result.push(`text-${propValue}`)
        } else {
          result.push(`text-${breakpoint}-${propValue}`)
        }
      }
      return result
    }, []))(props, 'label')
)
const labelColProps = computed(() => getColProps(props, 'label'))
const isHorizontal = computed(
  () => Object.keys(contentColProps.value).length > 0 || Object.keys(labelColProps.value).length > 0
)
const isFloating = computed(() => props.floating && !isHorizontal.value)

const stateClass = useStateClass(computedState)
const computedAriaInvalid = useAriaInvalid(() => props.ariaInvalid, computedState)

const onLegendClick = (event: Readonly<MouseEvent>) => {
  if (computedLabelFor.value || content.value === null) return

  const {target} = event
  const tagName = target ? (target as HTMLElement).tagName : ''

  if ([...INPUTS, 'a', 'button', 'label'].indexOf(tagName) !== -1) return

  // In horizontal mode, content.value is a BCol component instance, not a DOM element
  // Access the DOM element via $el property
  const contentElement =
    isHorizontal.value && content.value && '$el' in content.value
      ? (content.value.$el as HTMLElement)
      : (content.value as HTMLDivElement | null)
  if (!contentElement) return

  const inputs = [
    ...contentElement.querySelectorAll(INPUTS.map((v) => `${v}:not([disabled])`).join()),
  ].filter(isVisible)
  const [inp] = inputs
  if (inputs.length === 1 && inp instanceof HTMLElement) {
    attemptFocus(inp)
  }
}

const computedId = useId(() => props.id)
const labelId = useId(undefined, '_BV_label_')
const labelTag = computed(() => (!isFieldset.value ? 'label' : 'legend'))
const labelClasses = computed(() => [
  isHorizontal.value ? 'col-form-label' : 'form-label',
  {
    'bv-no-focus-ring': !isFloating.value && !computedLabelFor.value,
    'col-form-label': isHorizontal.value || (!isFloating.value && !computedLabelFor.value),
    'pt-0': !isHorizontal.value && !isFloating.value && !computedLabelFor.value,
    'd-block': !isHorizontal.value && (isFloating.value || !!computedLabelFor.value),
    [`col-form-label-${props.labelSize}`]: !!props.labelSize,
    'visually-hidden': props.labelVisuallyHidden,
  },
  isHorizontal.value ? null : labelAlignClasses.value,
  props.labelClass,
])

const invalidFeedbackId = useId(undefined, '_BV_feedback_invalid_')

const validFeedbackId = useId(undefined, '_BV_feedback_valid_')
const descriptionId = useId(undefined, '_BV_description_')

const isFieldset = computed(() => !isFloating.value && !computedLabelFor.value)

const hasDescription = computed(() => !!slots.description || !!props.description)
const hasInvalidFeedback = computed(() => !!slots['invalid-feedback'] || !!props.invalidFeedback)
const hasValidFeedback = computed(() => !!slots['valid-feedback'] || !!props.validFeedback)

const computedAriaDescribedby = computed<string | null>(() => {
  const ids: string[] = []
  if (hasDescription.value) ids.push(descriptionId.value)
  if (computedState.value === false && hasInvalidFeedback.value) ids.push(invalidFeedbackId.value)
  if (computedState.value === true && hasValidFeedback.value) ids.push(validFeedbackId.value)
  return ids.length > 0 ? ids.join(' ') : null
})

const labelShowing = computed(() => !!slots.label || !!props.label || isHorizontal.value)

const labelComponentProps = computed(() => ({
  label: props.label,
  labelTag: labelTag.value,
  labelId: labelId.value,
  computedLabelFor: computedLabelFor.value,
  isFieldset: isFieldset.value,
  isHorizontal: isHorizontal.value,
  labelColProps: labelColProps.value,
  labelAlignClasses: labelAlignClasses.value,
  labelClasses: labelClasses.value,
}))

const contentComponentProps = computed(() => ({
  invalidFeedback: props.invalidFeedback,
  validFeedback: props.validFeedback,
  description: props.description,
  feedbackAriaLive: props.feedbackAriaLive,
  state: computedState.value,
  tooltip: props.tooltip,
  invalidFeedbackId: invalidFeedbackId.value,
  validFeedbackId: validFeedbackId.value,
  descriptionId: descriptionId.value,
}))
</script>
